import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { AdminProfileUpdate, UpdateAdmin } from '../../redux/adminSlice';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../../redux/blockingSlice';
import { removeAdminImage } from '../../redux/adminSlice';
import { Edit, X, Check, Loader2, Send, RefreshCw, CheckCircle } from 'lucide-react';
import getFileURL from '../../utils/fileURL';
import { checkEmailExists, checkMobileExists, sendOtp, verifyOtp } from '../../redux/authSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import GenderSelect from '../common/GenderSelect';
import { getGenderOptions } from '../../utils/personFields';
import CountrySelect from '../common/CountrySelect';
import CountryFlag from '../common/CountryFlag';
import { getCountries } from '../../redux/masterSlice';
import SignInContacts from '../common/SignInContacts';
import useCanManage from '../../hooks/useCanManage';
import useTenantLoginPolicy from '../../hooks/useTenantLoginPolicy';


const formatDobDisplay = (dob) => {
    if (!dob) return '-';
    let date;
    const str = String(dob).trim();
    if (str.includes('T') || /^\d{4}-\d{2}-\d{2}$/.test(str)) date = new Date(str);
    else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(str)) { const p = str.split(/[/-]/); date = new Date(`${p[2]}-${p[1]}-${p[0]}`); }
    else date = new Date(str);
    return isNaN(date?.getTime()) ? '-' : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const AdminProfileTab = ({ admin }) => {
    // The managed admin's tenant login policy decides which unverified contact
    // is red (a code is demanded) and which is grey. The Super Admin's own
    // profile has no tenant, so no policy is passed there (stays red).
    const tenantLoginPolicy = useTenantLoginPolicy(admin?.tenantId);
    const { t } = useTranslation('adminProfile');
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('tenants.manage');
    const { loading } = useSelector((state) => state.admin);
    const { removeloading } = useSelector((state) => state.doctor);
    const { countries } = useSelector((state) => state.master);
    const { trynavigate } = useSelector((state) => state.blocking);
    const fileInputRef = useRef();
    const [isEditMode, setIsEditMode] = useState(false);
    // Prefer the loaded profile's own id (so a super-admin editing another admin
    // updates the right record); fall back to the logged-in user's id.
    const adminId = admin?.adminId || localStorage.getItem('ID');

    const [formData, setFormData] = useState({
        name: '', surname: '', gender: '', dob: '', email: '',
        phone: '',
        altPhone: '',
        description: '',
        countryId: '',
    });
    const [errors, setErrors] = useState({});
    const [charLimitReached, setCharLimitReached] = useState({});
    const [profileImage, setProfileImage] = useState(null);

    const axiosInstance = useAxiosAuth();
    const { emailCheckLoading, mobileCheckLoading, sendOtpLoading, verifyOtpLoading } = useSelector((state) => state.auth || {});

    // Phone logic states
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress

    // Phone OTP states
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [phoneOtpCode, setPhoneOtpCode] = useState('');
    const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
    const [phoneOtpError, setPhoneOtpError] = useState('');
    const [phoneOtpTimer, setPhoneOtpTimer] = useState(0);
    const phoneOtpTimerRef = useRef(null);
    const phoneOtpVerifyingRef = useRef(false);

    // Email logic states
    const [emailStatus, setEmailStatus] = useState(null);
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef('');
    const isEmailCheckInProgressRef = useRef(false);

    // Email OTP states
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [emailOtpVerified, setEmailOtpVerified] = useState(false);
    const [emailOtpError, setEmailOtpError] = useState('');
    const [emailOtpTimer, setEmailOtpTimer] = useState(0);
    const emailOtpTimerRef = useRef(null);
    const emailOtpVerifyingRef = useRef(false);

    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    // OTP related functions
    const resetOtpEmailState = () => {
        setEmailOtpSent(false);
        setEmailOtpCode('');
        setEmailOtpVerified(false);
        setEmailOtpError('');
        setEmailOtpTimer(0);
        if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
        emailOtpVerifyingRef.current = false;
    };

    const resetOtpPhoneState = () => {
        setPhoneOtpSent(false);
        setPhoneOtpCode('');
        setPhoneOtpVerified(false);
        setPhoneOtpError('');
        setPhoneOtpTimer(0);
        if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
        phoneOtpVerifyingRef.current = false;
    };

    const startOtpTimer = (setTimer, timerRef) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(60);
        timerRef.current = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleSendPhoneOtp = async () => {
        setPhoneOtpError('');
        try {
            const result = await dispatch(sendOtp({ phone: formData.phone.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpSent(true);
                setPhoneOtpCode('');
                setPhoneOtpVerified(false);
                startOtpTimer(setPhoneOtpTimer, phoneOtpTimerRef);
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('adminProfile.failed_send_otp'));
        }
    };

    const handleVerifyPhoneOtp = useCallback(async (code) => {
        const otpVal = code ?? phoneOtpCode;
        if (phoneOtpVerifyingRef.current || otpVal.length !== 6) return;
        phoneOtpVerifyingRef.current = true;
        setPhoneOtpError('');
        try {
            const result = await dispatch(verifyOtp({ phone: formData.phone.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setPhoneOtpVerified(true);
                if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
                setPhoneOtpTimer(0);
            } else {
                setPhoneOtpError(t('adminProfile.invalid_otp'));
            }
        } catch (err) {
            setPhoneOtpError(typeof err === 'string' ? err : t('adminProfile.invalid_otp'));
        } finally {
            phoneOtpVerifyingRef.current = false;
        }
    }, [phoneOtpCode, dispatch, axiosInstance, formData.phone]);

    const handleSendEmailOtp = async () => {
        setEmailOtpError('');
        try {
            const result = await dispatch(sendOtp({ email: formData.email.trim(), axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpSent(true);
                setEmailOtpCode('');
                setEmailOtpVerified(false);
                startOtpTimer(setEmailOtpTimer, emailOtpTimerRef);
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('adminProfile.failed_send_otp'));
        }
    };

    const handleVerifyEmailOtp = useCallback(async (code) => {
        const otpVal = code ?? emailOtpCode;
        if (emailOtpVerifyingRef.current || otpVal.length !== 6) return;
        emailOtpVerifyingRef.current = true;
        setEmailOtpError('');
        try {
            const result = await dispatch(verifyOtp({ email: formData.email.trim(), otpCode: otpVal, axiosInstance })).unwrap();
            if (result?.success) {
                setEmailOtpVerified(true);
                if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
                setEmailOtpTimer(0);
            } else {
                setEmailOtpError(t('adminProfile.invalid_otp'));
            }
        } catch (err) {
            setEmailOtpError(typeof err === 'string' ? err : t('adminProfile.invalid_otp'));
        } finally {
            emailOtpVerifyingRef.current = false;
        }
    }, [emailOtpCode, dispatch, axiosInstance, formData.email]);

    // Cleanup OTP timers on unmount
    useEffect(() => {
        return () => {
            if (phoneOtpTimerRef.current) clearInterval(phoneOtpTimerRef.current);
            if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);
            if (emailOtpTimerRef.current) clearInterval(emailOtpTimerRef.current);
            if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current);
        };
    }, []);

    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Debounced email check
    useEffect(() => {
        if (!isEditMode) return;

        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';
        const originalEmail = admin?.email || '';

        if (originalEmail && email === originalEmail) {
            setEmailStatus(true);
            setEmailOtpVerified(true);
            lastCheckedEmailRef.current = email;
            return;
        }

        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            setEmailOtpVerified(false);
            lastCheckedEmailRef.current = '';
            return;
        }

        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        setEmailStatus(!resultAction.payload.exists);
                        if (resultAction.payload.exists) {
                            setEmailOtpVerified(false);
                        }
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = '';
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
            }
        }, 500);
    }, [formData.email, isEditMode, admin?.email]);

    // Debounced phone check
    useEffect(() => {
        if (!isEditMode) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';
        const originalPhone = admin?.phone || '';

        // Treat original phone as valid
        if (originalPhone && phone === originalPhone) {
            setPhoneStatus(true);
            setPhoneOtpVerified(true);
            lastCheckedPhoneRef.current = phone;
            return;
        }

        // Must be valid phone formatting first
        const isValidFormat = /^\d{10}$/.test(phone);

        if (!phone || !isValidFormat) {
            setPhoneStatus(null);
            setPhoneOtpVerified(false);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();
            if (!trimmedPhone || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;

            try {
                const resultAction = await dispatchRef.current(checkMobileExists({
                    phone: trimmedPhone,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.phone.trim() === trimmedPhone) {
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        setPhoneStatus(!resultAction.payload.exists);
                        if (resultAction.payload.exists) {
                            setPhoneOtpVerified(false);
                        }
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (formData.phone.trim() === trimmedPhone) {
                    setPhoneStatus(null);
                    lastCheckedPhoneRef.current = '';
                }
            } finally {
                isPhoneCheckInProgressRef.current = false;
            }
        }, 500);
    }, [formData.phone, isEditMode, admin?.phone]);

    // Modal & Cropper States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Helper function to format date for input (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
        return dateString ? dateString.split('T')[0] : '';
    };

    // Ensure the countries list is available for the Country dropdown.
    useEffect(() => {
        if (!countries || countries.length === 0) {
            dispatch(getCountries({ status: 'active', limit: 100 }));
        }
    }, [dispatch, countries]);

    // Populate form when admin data loads
    useEffect(() => {
        if (admin) {
            setFormData({
                name: admin.name || '',
                surname: admin.surname || '',
                gender: admin.gender || '',
                dob: formatDateForInput(admin.dob),
                email: admin.email || '',
                phone: admin.phone || '',
                altPhone: admin.altPhone || '',
                description: admin.description || '',
                countryId: admin.countryId || admin.country?.countryId || '',
            });
            setProfileImage(admin.image || null);
        }
    }, [admin]);

    // Navigation blocking
    useEffect(() => {
        if (trynavigate && isEditMode) {
            Swal.fire({
                title: t('adminProfile.editing_in_progress'),
                text: t('adminProfile.editing_in_progress_text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('adminProfile.abort'),
                confirmButtonColor: '#4ABDBD',
                cancelButtonText: t('adminProfile.cancel'),
                cancelButtonColor: '#4ABDBD',
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    // Discard — preserve pendingNavigation for the parent consumer.
                    dispatch(tryingnavigate(false));
                    handleCancelEdit();
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    // Stay — drop the captured destination.
                    dispatch(tryingnavigate(false));
                    dispatch(clearPendingNavigation());
                }
            });
        }
    }, [trynavigate, dispatch, t, isEditMode]);

    // Open modal
    const handleEditClick = () => {
        setIsModalOpen(true);
        setSelectedFile(null);
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setIsCropping(false);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

        // Validate file type
        if (!validTypes.includes(file.type)) {
            toast.warn(t('adminProfile.invalidfiletype'));
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            toast.warn(t('adminProfile.image_size_exceed'));
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result);
            setSelectedFile(file);
            setIsCropping(true);
        };
        reader.readAsDataURL(file);
    };

    // Crop complete callback
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Generate cropped image
    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = new Image();
        image.src = imageSrc;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg');
        });
    };

    // Submit cropped image
    const handleSubmitCrop = async () => {
        if (!croppedAreaPixels || !imageSrc) return;

        setIsUploading(true);
        try {
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const file = new File([croppedBlob], selectedFile.name, { type: 'image/jpeg' });
            const formData = new FormData();
            formData.append('file', file);

            await dispatch(AdminProfileUpdate({ admId: adminId, formData })).unwrap();

            const newImageUrl = URL.createObjectURL(croppedBlob);
            setProfileImage(newImageUrl);

            toast.success(t('adminProfile.upload_success_text'));

            setIsModalOpen(false);
        } catch (err) {
            toast.warn(t('adminProfile.upload_error_text'));
        } finally {
            setIsUploading(false);
        }
    };

    // Remove image
    const handleRemoveImage = async () => {
        Swal.fire({
            title: t('adminProfile.remove_image_title'),
            text: t('adminProfile.remove_image_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('adminProfile.remove'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonText: t('adminProfile.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await dispatch(removeAdminImage(adminId)).unwrap();

                    setProfileImage(null);
                    setIsModalOpen(false);

                    toast.success(t('adminProfile.remove_success_text'));
                } catch (error) {
                    toast.error(t('adminProfile.remove_error_text'));
                }
            }
        });
    };

    // Close modal
    const closeModal = () => {
        setIsModalOpen(false);
        setImageSrc(null);
        setSelectedFile(null);
        setIsCropping(false);
    };

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = t('adminProfile.nameisrequired');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('adminProfile.name_min_2_chars');
        } else if (formData.name.length > 25) {
            newErrors.name = t('adminProfile.nameexceed');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.name.trim())) {
            newErrors.name = t('adminProfile.name_invalid_chars');
        }

        // Surname validation
        if (!formData.surname.trim()) {
            newErrors.surname = t('adminProfile.surnameisrequired');
        } else if (formData.surname.trim().length < 2) {
            newErrors.surname = t('adminProfile.surname_min_2_chars');
        } else if (formData.surname.length > 25) {
            newErrors.surname = t('adminProfile.surnameexceed');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.surname.trim())) {
            newErrors.surname = t('adminProfile.surname_invalid_chars');
        }

        // Description validation
        if (formData.description && formData.description.length > 100) {
            newErrors.description = t('adminProfile.descriptionwarn');
        } else if (formData.description && formData.description.trim().length === 0 && formData.description.length > 0) {
            newErrors.description = t('adminProfile.description_only_spaces');
        }

        // Phone validation (if not disabled, or if it's part of the data to be sent)
        if (!formData.phone || !formData.phone.trim()) {
            newErrors.phone = t('adminProfile.phoneisrequired');
        } else if (formData.phone && formData.phone.trim()) {
            if (!/^\d{10}$/.test(formData.phone)) {
                newErrors.phone = t('adminProfile.error_phone_india');
            } else if (phoneStatus === false) {
                newErrors.phone = t('adminProfile.phone_already_exists');
            } else if (phoneStatus === null && !mobileCheckLoading) {
                newErrors.phone = t('adminProfile.please_verify_phone');
            } else if (phoneStatus === true && !phoneOtpVerified) {
                newErrors.phone = t('adminProfile.verify_phone_otp');
            }
        }

        // Alt Phone validation (optional)
        if (formData.altPhone && formData.altPhone.trim()) {
            if (!/^\d{10}$/.test(formData.altPhone)) {
                newErrors.altPhone = t('adminProfile.error_phone_india');
            }
        }

        // Email validation (when changed)
        if (formData.email && formData.email.trim() && admin?.email !== formData.email.trim()) {
            const emailVal = formData.email.trim();
            const emailRegexLocal = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegexLocal.test(emailVal)) {
                newErrors.email = t('adminProfile.invalid_email_format');
            } else if (emailStatus === false) {
                newErrors.email = t('adminProfile.email_already_exists');
            } else if (emailStatus === null && !emailCheckLoading) {
                newErrors.email = t('adminProfile.please_verify_email');
            } else if (emailStatus === true && !emailOtpVerified) {
                newErrors.email = t('adminProfile.verify_email_otp');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const maxLengths = { name: 25, surname: 25, description: 100, phone: 10, altPhone: 10 };

        let processedValue = value;

        // Capitalize first letter for name and surname
        if (name === 'name' || name === 'surname') {
            if (processedValue.length > 0) {
                processedValue = processedValue.charAt(0).toUpperCase() + processedValue.slice(1);
            }
        }

        // Sanitize input - remove dangerous characters
        if (name === 'name' || name === 'surname') {
            // Remove dangerous characters and restrict to letters, spaces, hyphens, apostrophes
            processedValue = processedValue.replace(/[<>]/g, '');
            // Only allow letters, spaces, hyphens, and apostrophes
            if (processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) {
                return; // Don't update if invalid characters
            }
            // Enforce max length
            if (processedValue.length > maxLengths[name]) {
                processedValue = processedValue.substring(0, maxLengths[name]);
            }
        } else if (name === 'description') {
            // Remove dangerous characters
            processedValue = processedValue.replace(/[<>]/g, '');
            // Enforce max length
            if (processedValue.length > maxLengths[name]) {
                processedValue = processedValue.substring(0, maxLengths[name]);
            }
        } else if (name === "phone" || name === "altPhone") {
            // For phone and altPhone, only allow numbers
            processedValue = value.replace(/[^0-9]/g, '');
            // Enforce max length
            if (processedValue.length > maxLengths[name]) {
                processedValue = processedValue.substring(0, maxLengths[name]);
            }
        } else {
            processedValue = value;
        }

        setFormData({ ...formData, [name]: processedValue });

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }

        // Check character limit
        if (maxLengths[name] && processedValue.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }
    };

    // Submit personal info
    const handleSubmitPersonalInfo = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const result = await Swal.fire({
                title: t('adminProfile.confirm_update_title'),
                text: t('adminProfile.confirm_update_text'),
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#d33',
                confirmButtonText: t('adminProfile.yes_update'),
            });

            if (result.isConfirmed) {
                try {
                    const editedData = {
                        ...formData,
                        ...(emailOtpVerified && { emailVerified: true }),
                        ...(phoneOtpVerified && { phoneNoVerified: true }),
                    };
                    await dispatch(UpdateAdmin({ admId: adminId, editedData })).unwrap();
                    toast.success(t('adminProfile.update_success_text'));
                    dispatch(restrictnavigate(false));
                    setIsEditMode(false);
                } catch (err) {
                    toast.warn(t('adminProfile.update_error_text'));
                }
            }
        }
    };

    // Toggle edit mode
    const handlePersonalInfoEdit = () => {
        setIsEditMode((prev) => !prev);
        dispatch(restrictnavigate(true));
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        dispatch(restrictnavigate(false));
        setFormData({
            name: admin?.name || '',
            surname: admin?.surname || '',
            gender: admin?.gender || '',
            dob: formatDateForInput(admin?.dob),
            email: admin?.email || '',
            phone: admin?.phone || '',
            altPhone: admin?.altPhone || '',
            description: admin?.description || '',
            countryId: admin?.countryId || admin?.country?.countryId || '',
        });
        setErrors({});
        setCharLimitReached({});
        setPhoneStatus(null);
        resetOtpPhoneState();
        lastCheckedPhoneRef.current = '';
        setEmailStatus(null);
        resetOtpEmailState();
        lastCheckedEmailRef.current = '';
    };

    return (
        <>
            {/* Profile Picture */}
            <div data-testid="admin-profile-avatar-section" className="flex justify-center my-6">
                <div className="relative">
                    <div data-testid="admin-profile-avatar" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300 overflow-hidden">
                        {profileImage ? (
                            <img data-testid="admin-profile-avatar-image" src={getFileURL(profileImage)} alt={t('adminProfile.profile_alt')} className="w-full h-full object-cover" />

                        ) : (
                            <span data-testid="admin-profile-avatar-initial" className="text-gray-500 text-2xl sm:text-3xl font-medium">
                                {formData?.name?.charAt(0)?.toUpperCase()}
                            </span>
                        )}
                    </div>
                    {canManage && (
                    <button
                        data-testid="admin-profile-avatar-edit-button"
                        onClick={handleEditClick}
                        className="absolute bottom-0 right-0 bg-[#4ABDBD] text-white p-1.5 rounded-b-full rounded-tr-full hover:bg-[#3AA8A8] transition-colors cursor-pointer"
                    >
                        <Edit data-testid="admin-profile-avatar-edit-icon" className="h-4 w-4" />
                    </button>
                    )}
                </div>
            </div>

            {/* Personal Information Section */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="personal-details-heading">
                    {t('adminProfile.heading1')}
                </h2>
                {canManage && !isEditMode && (
                    <button
                        onClick={handlePersonalInfoEdit}
                        disabled={loading}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="edit-personal-info-button"
                    >
                        {loading ? (
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : (
                            <Edit className="h-5 w-5" />
                        )}
                        <span data-testid="edit-text">{t('adminProfile.edit')}</span>
                    </button>
                )}
            </div>

            {isEditMode ? (
                <form data-testid="personal-info-form" onSubmit={handleSubmitPersonalInfo} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="name-label-edit">
                            {t('adminProfile.name')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('adminProfile.nametext')}
                            maxLength={25}
                            data-testid="name-input"
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-1" data-testid="name-error">{errors.name}</p>}
                        {charLimitReached.name && <p data-testid="admin-profile-name-char-limit-message" className="text-red-500 text-sm mt-1">{t('adminProfile.reached_text_limit', { limit: 25 })}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="surname-label-edit">
                            {t('adminProfile.surname')} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="surname"
                            value={formData.surname}
                            onChange={handleInputChange}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('adminProfile.surnametext')}
                            maxLength={25}
                            data-testid="surname-input"
                        />
                        {errors.surname && <p className="text-red-500 text-sm mt-1" data-testid="surname-error">{errors.surname}</p>}
                        {charLimitReached.surname && <p data-testid="admin-profile-surname-char-limit-message" className="text-red-500 text-sm mt-1">{t('adminProfile.reached_text_limit', { limit: 25 })}</p>}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="gender-label-edit">
                            {t('adminProfile.gender')}
                        </label>
                        <GenderSelect
                            value={formData.gender}
                            onChange={(val) => {
                                setFormData(prev => ({ ...prev, gender: val }));
                                setErrors(prev => ({ ...prev, gender: null }));
                            }}
                            options={getGenderOptions(t)}
                            placeholder={t('adminProfile.gender')}
                            error={!!errors.gender}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="dob-label-edit">
                            {t('adminProfile.dob')}
                        </label>
                        <input
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleInputChange}
                            /* Cap at today — the patient/doctor forms already do. */
                            max={new Date().toISOString().split('T')[0]}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            data-testid="dob-input"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="country-label-edit">
                            {t('adminProfile.country', 'Country')}
                        </label>
                        <CountrySelect
                            value={formData.countryId}
                            onChange={(countryId) => {
                                setFormData(prev => ({ ...prev, countryId }));
                                setErrors(prev => ({ ...prev, countryId: null }));
                            }}
                            countries={countries}
                            placeholder={t('adminProfile.select_country', 'Select Country')}
                            error={!!errors.countryId}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="email-label-edit">
                            {t('adminProfile.email')}
                        </label>
                        <div className="relative">
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={(e) => {
                                    handleInputChange(e);
                                    if (e.target.value !== formData.email) {
                                        setEmailStatus(null);
                                        resetOtpEmailState();
                                    }
                                }}
                                disabled={emailOtpVerified && admin?.email !== formData.email}
                                maxLength={254}
                                className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.email ? 'border-red-500 bg-red-50' : emailStatus === false ? 'border-red-400 focus:border-red-400' : emailStatus === true && emailOtpVerified ? 'border-green-400 focus:border-green-400' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                                data-testid="email-input"
                            />
                            {formData.email && emailRegex.test(formData.email.trim()) && admin?.email !== formData.email && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {emailCheckLoading ? (
                                        <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                                    ) : emailStatus === true ? (
                                        emailOtpVerified ? (
                                            <CheckCircle className="h-5 w-5 text-green-500" />
                                        ) : null
                                    ) : emailStatus === false ? (
                                        <X className="h-5 w-5 text-red-500" />
                                    ) : null}
                                </div>
                            )}
                        </div>
                        {emailStatus === false && !errors.email && (
                            <p data-testid="admin-profile-email-exists-message" className="text-red-500 text-sm mt-1">{t('adminProfile.email_already_exists')}</p>
                        )}
                        {errors.email && <p className="text-red-500 text-sm mt-1" data-testid="email-error">{errors.email}</p>}

                        {/* Email OTP Verification UI */}
                        {formData.email && emailStatus === true && admin?.email !== formData.email && !emailOtpVerified && (
                            <div data-testid="admin-profile-email-otp-container" className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3">
                                <div className="flex justify-between items-center px-1">
                                    <span data-testid="admin-profile-email-otp-label" className="text-sm font-medium text-gray-700">{t('adminProfile.email_verification')}</span>
                                    {!emailOtpSent ? (
                                        <button
                                            data-testid="admin-profile-email-otp-send-button"
                                            type="button"
                                            onClick={handleSendEmailOtp}
                                            disabled={loading || sendOtpLoading}
                                            className="text-xs bg-[#4ABDBD]/10 text-[#4ABDBD] px-3 py-1.5 rounded-lg font-medium hover:bg-[#4ABDBD]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                            {t('adminProfile.send_otp')}
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            {emailOtpTimer > 0 ? (
                                                <span data-testid="admin-profile-email-otp-timer" className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                                    00:{emailOtpTimer < 10 ? `0${emailOtpTimer}` : emailOtpTimer}
                                                </span>
                                            ) : (
                                                <button
                                                    data-testid="admin-profile-email-otp-resend-button"
                                                    type="button"
                                                    onClick={handleSendEmailOtp}
                                                    disabled={loading || sendOtpLoading}
                                                    className="text-xs text-[#4ABDBD] hover:text-[#3AA8A8] font-medium flex items-center gap-1 p-1 hover:bg-[#4ABDBD]/10 rounded transition-colors"
                                                >
                                                    <RefreshCw className="w-3.5 h-3.5" />
                                                    {t('adminProfile.resend')}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {emailOtpSent && (
                                    <div className="relative flex items-center gap-2 mt-1">
                                        <input
                                            data-testid="admin-profile-email-otp-input"
                                            type="text"
                                            maxLength={6}
                                            value={emailOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                setEmailOtpCode(val);
                                                if (val.length === 6) {
                                                    handleVerifyEmailOtp(val);
                                                }
                                            }}
                                            placeholder={t('adminProfile.enter_6_digit_otp')}
                                            className={`flex-1 p-2.5 bg-white border rounded-lg text-sm text-center tracking-widest font-medium focus:outline-none focus:ring-2 transition-all shadow-sm ${emailOtpError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/20'}`}
                                            disabled={emailOtpVerifyingRef.current}
                                        />
                                        {emailOtpVerifyingRef.current && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                {emailOtpError && (
                                    <p data-testid="admin-profile-email-otp-error" className="text-red-500 text-xs font-medium px-1 flex items-center gap-1 mt-0.5">
                                        <X className="w-3 h-3" />
                                        {emailOtpError}
                                    </p>
                                )}
                            </div>
                        )}
                        {emailOtpVerified && admin?.email !== formData.email && (
                            <p data-testid="admin-profile-email-verified-message" className="text-green-600 text-sm mt-2 flex items-center gap-1.5 font-medium px-1">
                                <CheckCircle className="w-4 h-4" /> {t('adminProfile.email_verified')}
                            </p>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="phone-label-edit">
                            {t('adminProfile.phone')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={(e) => {
                                    handleInputChange(e);
                                    if (e.target.value.replace(/[^0-9]/g, '') !== formData.phone) {
                                        setPhoneStatus(null);
                                        resetOtpPhoneState();
                                    }
                                }}
                                maxLength={10}
                                className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.phone ? 'border-red-500 bg-red-50' : phoneStatus === false ? 'border-red-400 focus:border-red-400' : phoneStatus === true && phoneOtpVerified ? 'border-green-400 focus:border-green-400' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                                placeholder={t('adminProfile.phone_placeholder_india')}
                                data-testid="phone-input"
                            />
                            {formData.phone && /^\d{10}$/.test(formData.phone) && phoneStatus !== null && admin?.phone !== formData.phone && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {mobileCheckLoading ? (
                                        <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                                    ) : phoneStatus ? (
                                        <CheckCircle className="h-5 w-5 text-green-500" />
                                    ) : (
                                        <X className="h-5 w-5 text-red-500" />
                                    )}
                                </div>
                            )}
                        </div>
                        {phoneStatus === false && !errors.phone && (
                            <p data-testid="admin-profile-phone-exists-message" className="text-red-500 text-sm mt-1">{t('adminProfile.phone_already_exists')}</p>
                        )}
                        {errors.phone && <p className="text-red-500 text-sm mt-1" data-testid="phone-error">{errors.phone}</p>}

                        {/* OTP Verification UI */}
                        {formData.phone && phoneStatus === true && admin?.phone !== formData.phone && !phoneOtpVerified && (
                            <div data-testid="admin-profile-phone-otp-container" className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3">
                                <div className="flex justify-between items-center px-1">
                                    <span data-testid="admin-profile-phone-otp-label" className="text-sm font-medium text-gray-700">{t('adminProfile.phone_verification')}</span>
                                    {!phoneOtpSent ? (
                                        <button
                                            data-testid="admin-profile-phone-otp-send-button"
                                            type="button"
                                            onClick={handleSendPhoneOtp}
                                            disabled={loading}
                                            className="text-xs bg-[#4ABDBD]/10 text-[#4ABDBD] px-3 py-1.5 rounded-lg font-medium hover:bg-[#4ABDBD]/20 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                            {t('adminProfile.send_otp')}
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            {phoneOtpTimer > 0 ? (
                                                <span data-testid="admin-profile-phone-otp-timer" className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                                                    00:{phoneOtpTimer < 10 ? `0${phoneOtpTimer}` : phoneOtpTimer}
                                                </span>
                                            ) : (
                                                <button
                                                    data-testid="admin-profile-phone-otp-resend-button"
                                                    type="button"
                                                    onClick={handleSendPhoneOtp}
                                                    disabled={loading}
                                                    className="text-xs text-[#4ABDBD] hover:text-[#3AA8A8] font-medium flex items-center gap-1 p-1 hover:bg-[#4ABDBD]/10 rounded transition-colors"
                                                >
                                                    <RefreshCw className="w-3.5 h-3.5" />
                                                    {t('adminProfile.resend')}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {phoneOtpSent && (
                                    <div className="relative flex items-center gap-2 mt-1">
                                        <input
                                            data-testid="admin-profile-phone-otp-input"
                                            type="text"
                                            maxLength={6}
                                            value={phoneOtpCode}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                setPhoneOtpCode(val);
                                                if (val.length === 6) {
                                                    handleVerifyPhoneOtp(val);
                                                }
                                            }}
                                            placeholder={t('adminProfile.enter_6_digit_otp')}
                                            className={`flex-1 p-2.5 bg-white border rounded-lg text-sm text-center tracking-widest font-medium focus:outline-none focus:ring-2 transition-all shadow-sm ${phoneOtpError ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:border-[#4ABDBD] focus:ring-[#4ABDBD]/20'}`}
                                            disabled={phoneOtpVerifyingRef.current}
                                        />
                                        {phoneOtpVerifyingRef.current && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                {phoneOtpError && (
                                    <p data-testid="admin-profile-phone-otp-error" className="text-red-500 text-xs font-medium px-1 flex items-center gap-1 mt-0.5">
                                        <X className="w-3 h-3" />
                                        {phoneOtpError}
                                    </p>
                                )}
                            </div>
                        )}
                        {phoneOtpVerified && admin?.phone !== formData.phone && (
                            <p data-testid="admin-profile-phone-verified-message" className="text-green-600 text-sm mt-2 flex items-center gap-1.5 font-medium px-1">
                                <CheckCircle className="w-4 h-4" /> {t('adminProfile.phone_verified')}
                            </p>
                        )}
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="alt-phone-label-edit">
                            {t('adminProfile.alt_phone')}
                        </label>
                        <input
                            type="tel"
                            name="altPhone"
                            value={formData.altPhone}
                            onChange={handleInputChange}
                            maxLength={10}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('adminProfile.phone_placeholder_india')}
                            data-testid="alt-phone-input"
                        />
                        {errors.altPhone && <p className="text-red-500 text-sm mt-1" data-testid="alt-phone-error">{errors.altPhone}</p>}
                    </div>
                    <div className="mb-4 sm:col-span-2">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="description-label-edit">
                            {t('adminProfile.description')}
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className={`w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('adminProfile.descriptiontext')}
                            rows="4"
                            maxLength={100}
                            data-testid="description-textarea"
                        />
                        {errors.description && <p className="text-red-500 text-sm mt-1" data-testid="description-error">{errors.description}</p>}
                        {charLimitReached.description && <p data-testid="admin-profile-description-char-limit-message" className="text-red-500 text-sm mt-1">{t('adminProfile.reached_text_limit', { limit: 100 })}</p>}
                    </div>
                    <div className="sm:col-span-2 flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={loading}
                            className={`px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            data-testid="cancel-personal-info-button"
                        >
                            {t('adminProfile.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold relative overflow-hidden text-sm ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                            data-testid="save-personal-info-button"
                        >
                            {loading ? (
                                <svg
                                    className="animate-spin h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                            ) : (
                                t('adminProfile.savechanges')
                            )}
                        </button>
                    </div>
                </form>
            ) : (
                <div data-testid="personal-info-display" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="name-label">
                            {t('adminProfile.name')}
                        </label>
                        <p className="text-gray-800" data-testid="name-value">{formData.name}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="surname-label">
                            {t('adminProfile.surname')}
                        </label>
                        <p className="text-gray-800" data-testid="surname-value">{formData.surname}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="gender-label">
                            {t('adminProfile.gender')}
                        </label>
                        <p className="text-gray-800" data-testid="gender-value">{t(`adminProfile.${formData.gender}`)}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="dob-label">
                            {t('adminProfile.dob')}
                        </label>
                        <p className="text-gray-800" data-testid="dob-value">{formatDobDisplay(formData.dob)}</p>
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="country-label">
                            {t('adminProfile.country', 'Country')}
                        </label>
                        {(() => {
                            const countryName = admin?.country?.countryName
                                || countries?.find(c => c.countryId === formData.countryId)?.countryName
                                || '';
                            return countryName ? (
                                <div className="flex items-center gap-2" data-testid="country-value">
                                    <CountryFlag countryName={countryName} className="w-5 h-3.5 object-cover rounded-sm" />
                                    <p className="text-gray-800">{countryName}</p>
                                </div>
                            ) : (
                                <p className="text-gray-800" data-testid="country-value">-</p>
                            );
                        })()}
                    </div>
                    {/* E-mail and mobile are drawn under "How you sign in" below,
                        with their pill — once, not twice (1 Oct 2026). */}
                    <div className="mb-4">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="alt-phone-label">
                            {t('adminProfile.alt_phone')}
                        </label>
                        <p className="text-gray-800" data-testid="alt-phone-value">{formData.altPhone || '-'}</p>
                    </div>
                    <div className="mb-4 sm:col-span-2">
                        <label className="block text-gray-400 font-medium mb-1" data-testid="description-label">
                            {t('adminProfile.description')}
                        </label>
                        <p className="text-gray-800" data-testid="description-value">{formData.description || '-'}</p>
                    </div>
                </div>
            )}

            {/* HOW THEY SIGN IN (owner, 1 Oct 2026) — view mode, so an address
                already on the record can be verified without editing it. The
                save carries that one field and its proof; the pill is the
                server's flag, re-read by UpdateAdmin's getAdminById. */}
            {!isEditMode && admin && (
                <SignInContacts
                    subject={admin}
                    policy={admin?.tenantId ? tenantLoginPolicy : undefined}
                    canWrite={canManage}
                    own={!admin?.adminId || String(admin.adminId) === String(localStorage.getItem('ID'))}
                    testIdPrefix="admin-signin"
                    onSave={(body) => dispatch(UpdateAdmin({ admId: adminId, editedData: body })).unwrap()}
                />
            )}

            {/* Image Crop Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="profile-image-modal-title"
                    id="profile-image-modal"
                    data-testid="profile-image-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col overflow-hidden">

                        {/* Header - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2
                                data-testid="admin-profile-image-modal-title"
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="profile-image-modal-title"
                            >
                                {t('adminProfile.profile_image_update')}
                            </h2>

                            <button
                                data-testid="admin-profile-image-modal-close-button"
                                onClick={closeModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Body */}
                        <div data-testid="admin-profile-image-modal-body" className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">

                            {/* Image Input */}
                            <div className="mb-4">
                                <label data-testid="admin-profile-image-upload-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                    {t('adminProfile.select_image')} <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        data-testid="admin-profile-image-upload-input"
                                        id="admin-profile-image-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                    <label
                                        data-testid="admin-profile-image-upload-trigger"
                                        htmlFor="admin-profile-image-upload"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                    >
                                        <span data-testid="admin-profile-image-upload-button-text" className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold">
                                            {t('filestab.choose_file')}
                                        </span>
                                        <span data-testid="admin-profile-image-upload-filename" className="text-sm text-gray-500 truncate">
                                            {selectedFile ? selectedFile.name : t('filestab.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {/* Cropper Preview */}
                            {imageSrc && (
                                <div data-testid="admin-profile-image-cropper-container" className="relative h-64 bg-gray-100 rounded-xl overflow-hidden shadow-inner border border-gray-200 mb-4">
                                    <Cropper
                                        image={imageSrc}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={1}
                                        onCropChange={setCrop}
                                        onZoomChange={setZoom}
                                        onCropComplete={onCropComplete}
                                        cropShape="round"
                                        showGrid={false}
                                    />
                                </div>
                            )}

                        </div>

                        {/* Footer - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">

                            {/* Cancel */}
                            <button
                                data-testid="admin-profile-image-modal-cancel-button"
                                onClick={closeModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                            >
                                {t('adminProfile.cancel')}
                            </button>

                            {/* If profile already exists show Remove */}
                            {profileImage && (
                                <button
                                    data-testid="admin-profile-image-modal-remove-button"
                                    onClick={handleRemoveImage}
                                    disabled={removeloading}
                                    className={`px-4 py-2 rounded-xl cursor-pointer text-white font-semibold text-sm shadow-lg transition-all duration-300
        ${removeloading
                                            ? 'bg-red-300 cursor-not-allowed opacity-60'
                                            : 'bg-gradient-to-r from-red-400 to-red-600 hover:shadow-xl hover:scale-105'
                                        }`}
                                >
                                    {removeloading ? (
                                        <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                    ) : (
                                        t('adminProfile.remove')
                                    )}
                                </button>
                            )}

                            {/* Submit Button */}
                            <button
                                data-testid="admin-profile-image-modal-submit-button"
                                onClick={handleSubmitCrop}
                                disabled={!isCropping || isUploading}
                                className={`px-4 py-2 rounded-xl text-white font-semibold text-sm shadow-lg transition-all duration-300 ${!isCropping || isUploading
                                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                                    : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-xl hover:scale-105'
                                    }`}
                            >
                                {isUploading ? (
                                    <span className="border-2 border-white border-t-transparent rounded-full w-5 h-5 animate-spin inline-block"></span>
                                ) : (
                                    profileImage ? t('adminProfile.update') : t('adminProfile.submit')
                                )}
                            </button>

                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminProfileTab;

